// Enterprise Capabilities - Bento Grid Layout
import React from 'react';
import { Brain, Globe, Smartphone, Database, Cpu, Cloud, Lock, Workflow } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface Capability {
  title: string;
  description: string;
  icon: React.ReactNode;
  technologies: string[];
  size: 'large' | 'medium';
}

const capabilities: Capability[] = [
  {
    title: 'AI Integration',
    description: 'GPT, computer vision, NLP, and predictive analytics integrated into your workflows. Turn data into decisions.',
    icon: <Brain className="w-7 h-7" />,
    technologies: ['OpenAI', 'TensorFlow', 'PyTorch', 'LangChain'],
    size: 'large',
  },
  {
    title: 'Custom Web Apps',
    description: 'React, Node.js, and cloud-native architectures. Built for scale, designed for speed.',
    icon: <Globe className="w-7 h-7" />,
    technologies: ['React', 'TypeScript', 'Node.js', 'PostgreSQL'],
    size: 'large',
  },
  {
    title: 'Mobile Apps',
    description: 'iOS, Android, and cross-platform. Native performance, unified codebase.',
    icon: <Smartphone className="w-6 h-6" />,
    technologies: ['React Native', 'Swift', 'Kotlin'],
    size: 'medium',
  },
  {
    title: 'Data Systems',
    description: 'ETL pipelines, real-time analytics, and data warehousing at scale.',
    icon: <Database className="w-6 h-6" />,
    technologies: ['Snowflake', 'dbt', 'Kafka'],
    size: 'medium',
  },
  {
    title: 'Edge Computing',
    description: 'Low-latency processing at the edge for IoT and real-time applications.',
    icon: <Cpu className="w-6 h-6" />,
    technologies: ['AWS Lambda', 'Cloudflare Workers'],
    size: 'medium',
  },
  {
    title: 'Cloud Infrastructure',
    description: 'Multi-cloud architectures with auto-scaling and disaster recovery.',
    icon: <Cloud className="w-6 h-6" />,
    technologies: ['AWS', 'GCP', 'Azure'],
    size: 'medium',
  },
];

const CapabilityCard = ({ capability, index, isVisible }: { capability: Capability; index: number; isVisible: boolean }) => {
  const isLarge = capability.size === 'large';
  
  return (
    <div 
      className={`group relative rounded-2xl border border-white/[0.08] bg-[#111111] p-6 lg:p-8 transition-all duration-500 hover:border-white/20 hover:-translate-y-1 ${
        isLarge ? 'md:col-span-2' : ''
      }`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 100}ms`,
        boxShadow: '0 4px 24px -8px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Hover glow */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Icon */}
      <div 
        className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5"
        style={{
          boxShadow: '0 4px 16px -4px hsl(var(--primary) / 0.2)',
        }}
      >
        {capability.icon}
      </div>
      
      {/* Content */}
      <h3 className="text-xl font-bold text-white mb-2">{capability.title}</h3>
      <p className="text-white/60 leading-relaxed mb-5">{capability.description}</p>
      
      {/* Technologies */}
      <div className="flex flex-wrap gap-2">
        {capability.technologies.map((tech) => (
          <span 
            key={tech}
            className="px-3 py-1 text-xs font-medium text-white/50 bg-white/[0.04] rounded-full border border-white/[0.06]"
          >
            {tech}
          </span>
        ))}
      </div>
    </div>
  );
};

export const EnterpriseCapabilities = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  return (
    <section 
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a]"
    >
      <div className="container-professional">
        {/* Header */}
        <div 
          className="text-center mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <Workflow className="w-4 h-4" />
            Core Capabilities
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Full-Stack Enterprise{' '}
            <span className="text-primary">Technology</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            End-to-end capabilities to build, deploy, and scale your most critical systems.
          </p>
        </div>
        
        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-6">
          {capabilities.map((capability, index) => (
            <CapabilityCard 
              key={capability.title}
              capability={capability}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
        
        {/* Security badge */}
        <div 
          className="mt-12 flex justify-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 600ms',
          }}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.02] border border-white/[0.08]">
            <Lock className="w-4 h-4 text-primary" />
            <span className="text-sm text-white/60">
              SOC 2 compliant • GDPR ready • Enterprise-grade security
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
